import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProviderCard, { MarketplaceProvider } from './ProviderCard';
import SummaryCard, { MarketplaceMetrics } from './SummaryCard';
import AddServiceProviderModal from '../../../admin/business-directory/AddServiceProviderModal';
import AlumniDetailsModal from '../../../admin/business-directory/AlumniDetailsModal';
import ComposeEmailModal from '../../../admin/business-directory/ComposeEmailModal';
import { axiosClient } from '../../../../library/Service/axiosClient';
import CarouselComponent from '../../../card-controls/CarouselComponent';
import { SmartContext } from '../../../../library/Core/SmartContext';

interface MarketplaceSummaryResponse {
    hasListing: boolean;
    provider?: Record<string, any>;
    metrics?: Record<string, any>;
    providers?: Record<string, any>[];
}

interface MarketplaceSectionProps {
    instituteId?: string | number;
}

const imageBasePath = process.env.REACT_APP_IMAGE_BASEPATH;
const marketplaceIcon = `${imageBasePath}/icon-breifcase.svg`;
const maleProfileFallback = `${imageBasePath}/yellow-student-boy.svg`;
const femaleProfileFallback = `${imageBasePath}/yellow-student.svg`;

const chunkProviders = (providers: MarketplaceProvider[], size: number) => {
    const chunks: MarketplaceProvider[][] = [];

    for (let index = 0; index < providers.length; index += size) {
        chunks.push(providers.slice(index, index + size));
    }

    return chunks;
};

const normalizeProvider = (provider: Record<string, any>): MarketplaceProvider => ({
    id: provider?.id ?? provider?.providerId ?? '',
    userName: provider?.userName ?? provider?.user_name ?? '',
    businessName: provider?.businessName ?? provider?.business_name ?? '',
    location: provider?.location ?? [provider?.city, provider?.country].filter(Boolean).join(', '),
    category: provider?.category ?? '',
    experienceYears: Number(provider?.experienceYears ?? provider?.experience_years ?? 0),
    profilePicture: provider?.profilePicture ?? provider?.profile_picture ?? '',
    degree: provider?.degree ?? provider?.programName ?? '',
    year: provider?.year ?? provider?.passOutYear ?? ''
});

const normalizeMetrics = (metrics?: Record<string, any>): MarketplaceMetrics => ({
    listingViews: Number(metrics?.listingViews ?? metrics?.listing_views ?? 0),
    inquiriesReceived: Number(metrics?.inquiriesReceived ?? metrics?.inquiries_received ?? 0),
    responsesGiven: Number(metrics?.responsesGiven ?? metrics?.responses_given ?? 0)
});

const toComposeProvider = (provider: MarketplaceProvider) => ({
    providerId: String(provider.id || ''),
    alumniName: provider.userName || '',
    contactEmailId: '',
    category: provider.category || '',
    subcategory: ''
});

const MarketplaceSection = ({ instituteId }: MarketplaceSectionProps) => {
    const { state } = useContext(SmartContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [marketplaceData, setMarketplaceData] = useState<MarketplaceSummaryResponse | null>(null);
    const [isMarketplaceOpened, setIsMarketplaceOpened] = useState(true);

    useEffect(() => {
        const fetchMarketplaceSummary = async () => {
            try {
                setLoading(true);
                setError('');

                const response = await axiosClient().get(`${process.env.REACT_APP_COMMON_API}/alumni-marketplace/summary`, {
                    params: instituteId ? { instituteId } : undefined
                });
                setMarketplaceData(response.data?.data || response.data);
            } catch (err) {
                console.error('Error fetching marketplace summary', err);
                setError('Unable to load marketplace data right now.');
            } finally {
                setLoading(false);
            }
        };

        fetchMarketplaceSummary();
    }, [instituteId]);

    const providers = (marketplaceData?.providers || []).slice(0, 9).map(normalizeProvider);
    const providerRows = chunkProviders(providers, 3);
    const listingProvider = marketplaceData?.provider ? normalizeProvider(marketplaceData.provider) : null;
    const metrics = normalizeMetrics(marketplaceData?.metrics);
    const marketplaceCollapseId = `collapseMarketplace${instituteId || 'default'}`;
    const genderCode = state?.data?.profile?.genderCode ?? 'MALE';
    const ownListingFallbackImage = genderCode === 'MALE' ? maleProfileFallback : femaleProfileFallback;
    const ownListingProfileImage = state?.internal?.model?.profileImage || listingProvider?.profilePicture || '';

    const renderProviderRow = (providerRow: MarketplaceProvider[], rowIndex: number) => (
        <div className="row g-3" key={`provider-row-${rowIndex}`}>
            {providerRow.map((provider) => (
                <div className="col-lg-4 col-md-6" key={provider.id}>
                    <ProviderCard
                        provider={provider}
                        viewDetailsTrigger={
                            <AlumniDetailsModal
                                className="btn-skyblue btn-sm rounded-pill px-3 font-14 font-400 text-decoration-none"
                                modalTitle="View Details"
                                providerId={String(provider.id)}
                            />
                        }
                        sendEnquiryTrigger={
                            <ComposeEmailModal
                                btnTitle=""
                                className="btn btn-send py-0 ps-2 pe-2"
                                provider={toComposeProvider(provider)}
                                iconMain={<i className="icon-send me-1"></i>}
                            />
                        }
                    />
                </div>
            ))}
        </div>
    );

    return (
        <div className="col-12">
            <div className="lite-blue border-radius-6 py-4 px-4">
                <div className="row g-3 align-items-start">
                    <div className="col-auto">
                        <img src={marketplaceIcon} className="mt-1" width="31" alt="Marketplace" />
                    </div>
                    <div className="col">
                        <div className="font-22 font-600">Marketplace</div>
                        <p className="mb-0">
                            {marketplaceData?.hasListing
                                ? 'You have an existing Marketplace Listing.'
                                : 'Showcase your business to fellow alumni and your alma mater to build new business connections.'}
                        </p>
                    </div>
                    <div className="col-auto">
                        <button
                            className={`bg-transparent border-0 icon-down-arrow fs-4 mt-2 ${isMarketplaceOpened ? '' : 'collapsed'}`}
                            type="button"
                            data-bs-target={`#${marketplaceCollapseId}`}
                            aria-expanded={isMarketplaceOpened}
                            aria-controls={marketplaceCollapseId}
                            onClick={() => setIsMarketplaceOpened(!isMarketplaceOpened)}
                        />
                    </div>
                </div>

                <div className={`collapse ${isMarketplaceOpened ? 'show' : ''}`} id={marketplaceCollapseId}>
                    <hr className="my-4 border-color-0B34541A opacity-100" />

                    {loading && (
                        <div className="text-center py-4 font-16 font-500">Loading marketplace data...</div>
                    )}

                    {!loading && error && (
                        <div className="text-center py-4">
                            <div className="font-16 font-500 mb-3">{error}</div>
                            <button type="button" className="btn-darkblue mx-auto height-45" onClick={() => window.location.reload()}>
                                <span>Retry</span>
                            </button>
                        </div>
                    )}

                    {!loading && !error && marketplaceData?.hasListing && listingProvider && (
                        <div className="row g-3 justify-content-center">
                            <div className="col-lg-6 col-md-6">
                                <ProviderCard
                                    provider={listingProvider}
                                    actionLabel="View Your Listing"
                                    profileImageOverride={ownListingProfileImage}
                                    fallbackProfileImageOverride={ownListingFallbackImage}
                                    viewDetailsTrigger={
                                        <AlumniDetailsModal
                                            className="btn-skyblue btn-sm rounded-pill px-3 font-14 font-400 text-decoration-none"
                                            modalTitle="View Your Listing"
                                            providerId={String(listingProvider.id)}
                                        />
                                    }
                                    sendEnquiryTrigger={
                                        <ComposeEmailModal
                                            btnTitle=""
                                            className="btn btn-send py-0 ps-2 pe-2"
                                            provider={toComposeProvider(listingProvider)}
                                            iconMain={<i className="icon-send me-1"></i>}
                                        />
                                    }
                                />
                            </div>
                            <div className="col-lg-4 col-md-6">
                                <SummaryCard metrics={metrics} onViewReport={() => navigate('/alumni/service-provider-dashboard')} />
                            </div>
                        </div>
                    )}

                    {!loading && !error && !marketplaceData?.hasListing && providers.length > 0 && (
                        <>
                            <div className="graduation_carousel_wrap marketplace-carousel-wrap">
                                <CarouselComponent
                                    key={`MarketplaceCarousel-${instituteId || 'default'}`}
                                    data={providerRows}
                                    cardRenderer={renderProviderRow}
                                    itemsPerPage={1}
                                    itemsPerSlide={1}
                                    showCount={false}
                                    responsive={{
                                        0: { items: 1 },
                                        400: { items: 1 },
                                        600: { items: 1 },
                                        700: { items: 1 },
                                        1000: { items: 1 },
                                    }}
                                />
                            </div>

                            <div className="text-center mt-4">
                                <AddServiceProviderModal
                                    className="btn-darkblue mx-auto height-45 text-decoration-none d-inline-flex align-items-center justify-content-center px-4"
                                    modalTitle="List Your Service"
                                />
                            </div>
                        </>
                    )}

                    {!loading && !error && !marketplaceData?.hasListing && providers.length === 0 && (
                        <div className="text-center py-4">
                            <div className="font-16 font-500 mb-3">No marketplace listings are available right now.</div>
                            <AddServiceProviderModal
                                className="btn-darkblue mx-auto height-45 text-decoration-none d-inline-flex align-items-center justify-content-center px-4"
                                modalTitle="List Your Service"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MarketplaceSection;